import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  input,
  model,
  ModelSignal,
  OnChanges,
  OnInit,
  output, SimpleChanges,
  viewChild,
} from '@angular/core'
import { acquireFocusDelay } from './idr-filter-value.component'
import { FilterOperation, FilterParams, FilterParamValue } from '../shared/utilities/http-params'
import { FormsModule } from '@angular/forms'
import { NgbDatepickerModule, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap'
import { MatIcon } from '@angular/material/icon'
import { dateOnlyFormat } from '../shared/utilities/date'
import { IdrColumnSpec } from './idr-columns'
import { addDays, parseISO } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { isEqual } from 'lodash'

const parseDateString = (isoDateString = ''): NgbDateStruct | undefined => {
  const dateOnlyString = isoDateString.slice(0, 10)
  if (dateOnlyString.length < 10) {
    return undefined
  } else {
    const isoDate = parseISO(dateOnlyString)
    if (isNaN(isoDate.valueOf())) {
      return undefined
    } else {
      return {
        year: isoDate.getFullYear(),
        month: 1 + isoDate.getMonth(),
        day: isoDate.getDate(),
      }
    }
  }
}

type ModelType = NgbDateStruct | undefined

@Component({
  selector: 'app-idr-date-range',
  imports: [NgbDatepickerModule, FormsModule, MatIcon],
  template: `
    <div class="input-group">
      <input #min ngbDatepicker
          class="form-control"
          [placeholder]="minPlaceholder()"
          [(ngModel)]="minModel"
          #dMin="ngbDatepicker"
      />
      <button (click)="dMin.toggle()" class='btn btn-outline-secondary'>
        <mat-icon fontIcon="calendar_month"></mat-icon>
      </button>
    </div>
    @if (maxVisible()) {
      <div class="input-group">
        <input ngbDatepicker
            class="form-control"
            placeholder='max'
            [(ngModel)]="maxModel"
            #dMax="ngbDatepicker"
        />
        <button (click)="dMax.toggle()" class='btn btn-outline-secondary'>
          <mat-icon fontIcon="calendar_month"></mat-icon>
        </button>
      </div>
    }
  `,
  styles: `
    :host {
      display: flex;
      column-gap: 5px;
    }
    .input-group {
      flex-basis: 0;
      flex-grow: 1;
      input {
        margin-top: 0;
        font-size: 16px;
      }
      input::placeholder {
        opacity: 0.7;
      }
      .btn {
        border-radius: 0 6px 6px 0;
        padding: 5px 3px 0 3px;
        border-color: #c6c6c6;
      }
    }
  `,
})
export class IdrDateRangeFilterComponent implements OnInit, OnChanges, AfterViewInit {
  readonly row = input.required<FilterParams>()
  readonly columnSpec = input.required<IdrColumnSpec>()
  readonly grabFocus = input.required<boolean>()

  readonly rowUpdate = output<FilterParams>()

  protected readonly minModel = model<ModelType>(undefined)
  protected readonly maxModel = model<ModelType>(undefined)

  private readonly minDatePickerRef = viewChild.required<NgbInputDatepicker>('dMin')
  private readonly minRef = viewChild.required<ElementRef<HTMLInputElement>>('min')

  protected readonly stringValue = computed((): string => this.row().values[0].value!)

  protected readonly operator = computed((): FilterOperation =>
    this.row().values[0].operation!,
  )

  protected readonly maxVisible = computed((): boolean =>
    ['between=', '>=', '<='].includes(this.operator()),
  )

  protected readonly minPlaceholder = computed((): string =>
    this.maxVisible() ? 'min' : dateOnlyFormat,
  )

  ngOnInit() {
    this.minModel.subscribe(() => this.onModelChange('min'))
    this.maxModel.subscribe(() => this.onModelChange('max'))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['row']) {
      this.refreshModels()
    }
  }

  private refreshModels() {
    const [minStruct, maxStruct] = this.stringValue().split(',').map(parseDateString)

    const setValue = (model: ModelSignal<ModelType>, value: ModelType) => {
      if (!isEqual(model(), value)) {
        model.set(value)
      }
    }

    setValue(this.minModel, minStruct)
    setValue(this.maxModel, maxStruct)
  }

  ngAfterViewInit() {
    if (this.grabFocus()) {
      window.setTimeout(() => this.acquireFocus(), acquireFocusDelay)
    }
  }

  private toIso(minMax: 'min' | 'max'): string | undefined {
    const dateStruct = minMax === 'min' ? this.minModel() : this.maxModel()
    if (dateStruct) {
      const { year, month, day } = dateStruct
      const tzDate = new TZDate(year, month - 1, day, 'EST')
      const date = new Date(tzDate.toISOString())
      if (this.columnSpec().dataType === 'timestamp') {
        // for example:
        // min: '2025-12-19' becomes '2025-12-19T05:00:00Z'
        // max: '2025-12-21' becomes '2025-12-22T05:00:00Z' (end of 12/21 in EST)
        return (minMax === 'min' ? date : addDays(date, 1)).toISOString()
      } else {
        return date.toISOString().slice(0, 10)
      }
    }
  }

  private modelsToParamValue(minMax: 'min' | 'max'): FilterParamValue {
    const toParamValue = (min?: string, max?: string): FilterParamValue => {
      if (min) {
        if (max) {
          // don't accept max < min
          if (max < min) {
            // the value just edited wins - other will be cleared
            return toParamValue(minMax === 'min' ? min : undefined, minMax === 'min' ? undefined : max)
          } else {
            return { operation: 'between=', value: `${min},${max}` }
          }
        } else {
          const op = this.operator()
          return { operation: op === 'between=' ? '>=' : op, value: min }
        }
      } else if (max) {
        return { operation: '<=', value: max }
      } else {
        return { operation: this.operator(), value: '' }
      }
    }

    return toParamValue(this.toIso('min'), this.toIso('max'))
  }

  private onModelChange(minMax: 'min' | 'max') {
    const value = this.modelsToParamValue(minMax)
    this.rowUpdate.emit({
      ...this.row(),
      values: [value],
    })
  }

  private acquireFocus() {
    this.minRef().nativeElement.focus()
    this.minDatePickerRef().open()
  }
}
